import React, { useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import { Viewer, DEFAULTS } from '@photo-sphere-viewer/core'
import { MarkersPlugin } from '@photo-sphere-viewer/markers-plugin'
import { CompassPlugin } from '@photo-sphere-viewer/compass-plugin'

import '@photo-sphere-viewer/core/index.css'
import '@photo-sphere-viewer/markers-plugin/index.css'
import '@photo-sphere-viewer/compass-plugin/index.css'

import { Image360 } from '../../Screen/MapInitial/MapInitialViewController'
import NavigatorIcon from '../../Imagens/navigator.png'
import { CustomNavbarButton } from './PhotoSphereNavbar'

type Props = {
    main: Image360
    secundaries: Image360[]
    onNavigate: (img: Image360, oldImg: number) => void
}

export function PhotoSphere({ main, secundaries, onNavigate }: Props) {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const viewerRef = useRef<Viewer | null>(null)
    const navbarContainerRef = useRef<HTMLDivElement | null>(null)

    const firstLoadRef = useRef(true)
    const isChangingPanoramaRef = useRef(false)

    const CAMERA_YAW_OFFSET = 270 * Math.PI / 180

    function azimuteParaYaw(azimute: number, heading: number) {
        const diff = ((azimute - heading + 540) % 360) - 180
        return (diff * Math.PI) / 180 + CAMERA_YAW_OFFSET
    }

    useEffect(() => {
        if (!containerRef.current) return

        const navbarContainer = document.createElement('div')
        navbarContainerRef.current = navbarContainer

        const viewer = new Viewer({
            container: containerRef.current,
            plugins: [
                CompassPlugin.withConfig({ size: '100px' }),
                [MarkersPlugin, {}],
            ],
            navbar: [
                { content: navbarContainer },
                'caption',
                'fullscreen',
            ],
            keyboard: 'always',
            keyboardActions: {
                ...DEFAULTS.keyboardActions,
                f: (viewer, evt) => {
                    if (!evt.ctrlKey && !evt.altKey) {
                        viewer.toggleFullscreen()
                    }
                },
            },
        })

        viewerRef.current = viewer
        ReactDOM.render(<CustomNavbarButton viewer={viewer} />, navbarContainer)

        const markersPlugin = viewer.getPlugin(MarkersPlugin) as MarkersPlugin

        markersPlugin.addEventListener('select-marker', (e: any) => {
            const img = e.marker?.config?.data as Image360
            if (!img || !viewerRef.current) return

            onNavigate(img, main.sgepimagem360)
        })

        return () => {
            ReactDOM.unmountComponentAtNode(navbarContainer)
            viewer.destroy()
            viewerRef.current = null
        }
    }, [])


    useEffect(() => {
        if (!viewerRef.current) return

        const viewer = viewerRef.current
        const markersPlugin = viewer.getPlugin(MarkersPlugin) as MarkersPlugin
        const mainHeading = Number(main.heading)

        const currentPosition = viewer.getPosition()

        markersPlugin.clearMarkers()

        viewer.addEventListener('zoom-updated', () => {
            if (!isChangingPanoramaRef.current) return

            const zoom = viewer.getZoomLevel()
            if (zoom > 75) {
                viewer.zoom(75)
            }
        })
        isChangingPanoramaRef.current = true

        viewer.setPanorama(main.url, {
            showLoader: false,
            position: firstLoadRef.current ? undefined : currentPosition,
            transition: firstLoadRef.current ? false : {
                effect: 'fade',
                rotation: false,
                speed: 1400,
            },
        }).then(() => {
            isChangingPanoramaRef.current = false


            viewer.animate({
                zoom: 0,
                speed: 500,
            })

            if (firstLoadRef.current) {
                viewer.rotate({
                    yaw: CAMERA_YAW_OFFSET,
                    pitch: 0,
                })
                firstLoadRef.current = false
            }

            secundaries.forEach((img) => {
                if (typeof img.azimute !== 'number') return

                const yaw = azimuteParaYaw(img.azimute, mainHeading)

                markersPlugin.addMarker({
                    id: String(img.sgepimagem360),
                    position: { yaw, pitch: 0 },
                    size: { width: 40, height: 40 },
                    image: NavigatorIcon,
                    anchor: 'center center',
                    tooltip: '',//img.filename,
                    data: img,
                    hoverScale: { amount: 1.5, duration: 300 },
                })
            })
        })

    }, [main, secundaries])


    return <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
}
